import type { TopicExplainabilityTableState } from './topicExplainability.types';

const SOURCE_TO_DESTINATION_TITLE_MAP: Array<{ match: string; destination: string }> = [
  { match: '3d printing intensity', destination: 'Narrative Page Explainability' },
  { match: '3 d printing intensity', destination: 'Narrative Page Explainability' },
  { match: '3d printing sentiment', destination: 'Narrative Page Explainability' },
  { match: '3 d printing sentiment', destination: 'Narrative Page Explainability' },
  { match: 'narrative page intensity', destination: 'Narrative Page Explainability' },
  { match: 'narrative page sentiment', destination: 'Narrative Page Explainability' },
  { match: 'narrative page excess sentiment', destination: 'Narrative Page Explainability' },
  { match: 'narrative page explainability', destination: 'Narrative Page Explainability' },
];

const normalizeTitle = (title: string | null | undefined): string => {
  if (!title) return '';
  const withSpaces = String(title)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z0-9]+)/g, '$1 $2');
  return withSpaces
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ');
};

export const resolveExplainabilityDestinationTitle = (
  sourceTitle: string | null | undefined,
  explicitDestination?: string | null,
): string | null => {
  const normalizedExplicit = normalizeTitle(explicitDestination);
  if (normalizedExplicit) {
    return explicitDestination ?? null;
  }

  const normalizedSource = normalizeTitle(sourceTitle);
  if (!normalizedSource) {
    return null;
  }

  const mapped = SOURCE_TO_DESTINATION_TITLE_MAP.find(entry => normalizedSource.includes(entry.match));
  if (mapped?.destination) {
    return mapped.destination;
  }

  return null;
};

export const transformExplainabilityResponse = (payload: any): TopicExplainabilityTableState => {
  const columnNodes = Array.isArray(payload?.col_nodes) ? payload.col_nodes : [];
  const dataKeys = columnNodes.map((col: any, index: number) => {
    const rawName = col?.name ?? col?.display_name ?? col?.displayName ?? col?.id ?? `column_${index + 1}`;
    const safeName = String(rawName ?? '').trim();
    const normalized = safeName.replace(/\s+/g, '_');
    return normalized || `column_${index + 1}`;
  });

  const dataTypes = columnNodes.map((col: any) => (col?.type ? String(col.type).toLowerCase().trim() : undefined));

  const rawRows = Array.isArray(payload?.data) ? payload.data : [];
  const tableData = rawRows.map((row: any) => {
    const record: Record<string, unknown> = {};
    dataKeys.forEach((key: string, columnIndex: number) => {
      const value = Array.isArray(row) ? row[columnIndex] : row?.[key];
      if (value === undefined || value === null) {
        record[key] = '';
        return;
      }
      if (typeof value === 'object' && value !== null && 'text' in value && 'url' in value) {
        record[key] = {
          text: value.text,
          url: value.url,
        };
        return;
      }
      record[key] = value;
    });
    return record;
  });

  return {
    dataKeys,
    tableData,
    title: payload?.title ? String(payload.title) : 'Topic Explainability',
    subtitle: payload?.subtitle ? String(payload.subtitle) : undefined,
    dataTypes,
  };
};

export const normalizeExplainabilityTitle = normalizeTitle;
